import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { motion, type Variants } from 'framer-motion';
import { observer } from 'mobx-react';

import { useSounds } from '../../hooks/useSounds';
import { useFlag } from '../../services/launchDarkly';
import {
  isChallengeBundleItem,
  isExtraOfferBundleItem,
  isFreeSpinsBundleItem,
  isMetaGameBundleItem,
  isMetaGameFeatureBundleItem,
  isRipsPackBundleItem,
  isSnappyGiftBundleItem,
} from '../../stores/rewards/helpers';
import type { MetaGameTokenPayload } from '../../stores/rewards/types';
import { useStores } from '../../stores/useStores';
import ChallengeRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/ChallengeRewardComponent/ChallengeRewardComponent';
import ExtraOfferRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/ExtraOfferRewardComponent/ExtraOfferRewardComponent';
import MetaGameFeatureRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/MetaGameFeatureRewardComponent/MetaGameFeatureRewardComponent';
import MetaGameRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/MetaGameRewardComponent/MetaGameRewardComponent';
import {
  captureImgFlySource,
  isCrownRipsWidgetVisible,
} from '../EventTriggerDialog/gifts/GiftLayout/components/RipsPackClaimFly/useRipsPackClaimFly';
import RipsPackRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/RipsPackRewardComponent/RipsPackRewardComponent';
import { FRBWidget } from '../FRBWidget/FRBWidget';
import { SnappyGiftReward } from '../SnappyGiftReward/SnappyGiftReward';
import { SocialCoin } from '../SocialCoin/SocialCoin';
import { SweepCoin } from '../SweepCoin/SweepCoin';

import {
  containerVariants,
  FADE_UP_STAGGER_DELAY,
  INITIAL_ANIMATION_DURATION,
  itemVariants,
  SOUND_DELAY,
  STAGGER_DELAY,
  WAIT_TIME,
} from './animations';
import classes from './ClaimRewardsAnimation.module.scss';
import { CLAIM_REWARDS_ANIMATION_SOUNDS } from './sounds';

type RewardType =
  | 'SC'
  | 'GC'
  | 'freeSpins'
  | 'metagame'
  | 'metagameFeature'
  | 'snappyGift'
  | 'challenge'
  | 'extraOffer'
  | 'ripsPack';
const REWARDS_ORDER: RewardType[] = [
  'GC',
  'SC',
  'freeSpins',
  'metagame',
  'metagameFeature',
  'snappyGift',
  'challenge',
  'extraOffer',
  'ripsPack',
];

function RewardMotionWrapper({
  children,
  className,
  variants = itemVariants,
}: {
  children: React.ReactNode;
  className: string;
  variants?: Variants;
}) {
  return (
    <motion.div className={classNames(classes.rewardItem, className)} variants={variants}>
      {children}
    </motion.div>
  );
}

// The pack skips the fade-up exit: it stays in place while the coins float away, and the
// fly-to-widget clone then swaps in at its exact rect (see handleAnimationComplete).
const ripsPackItemVariants: Variants = { ...itemVariants, fadeUp: { y: 0, opacity: 1, scale: 1 } };

function ClaimRewardsAnimationComponent() {
  {
    const { playSound } = useSounds(CLAIM_REWARDS_ANIMATION_SOUNDS);

    const {
      uiStore,
      walletStore,
      userFreeSpinsStore,
      metagamesStore,
      animationStore,
      userSnappyGiftsStore,
      challengesStore,
      eventTriggerStore,
      crownRipsStore,
    } = useStores();
    const { claimRewardAnimationState } = uiStore;
    const [shouldFadeUp, setShouldFadeUp] = useState(false);
    const ripsPackTileRef = useRef<HTMLDivElement | null>(null);
    const isNewRewardsAnimationEnabled = useFlag('new_rewards_animation', false);

    // Calculate fontSize based on fixed container width (120px)
    // metagame: 19% of 120px = 22.8px, freeSpin: 10% of 120px = 12px
    const REWARD_ITEM_WIDTH = 120;
    const metagameFontSize = (19 / 100) * REWARD_ITEM_WIDTH; // ~22.8px
    const freeSpinFontSize = (8 / 100) * REWARD_ITEM_WIDTH; // 12px

    const { enable, rewards, refetchData, enableSounds, onEnd, hideOverlay } = claimRewardAnimationState;

    // The pack's fly-to-widget flourish only makes sense when the Crown Rips widget is on screen
    // (the lobby). Inside a slot there's no widget to fly to, so the pack should just float up like
    // every other reward tile. Captured once per open (presence can't change mid-run).
    const hasCrownRipsWidget = useMemo(() => (enable ? isCrownRipsWidgetVisible() : false), [enable]);

    // Dedupe by id, but only for items that carry one. Not every claim surface sends ids —
    // a challenge claim relays `{bundleType, bundlePayload}` — and treating those as all
    // sharing the id `undefined` collapsed a package's rewards down to a single tile.
    const bundleItems =
      rewards
        ?.flatMap((pkg) => pkg.bundleItems)
        .filter((item, index, self) => !item.id || index === self.findIndex((i) => i.id === item.id)) || [];

    const freeSpinPayloads = bundleItems?.filter(isFreeSpinsBundleItem).map((item) => item.bundlePayload);
    const snappyGiftPayloads = bundleItems?.filter(isSnappyGiftBundleItem).map((item) => item.bundlePayload);
    const challengeRewardPayloads = bundleItems?.filter(isChallengeBundleItem).map((item) => item.bundlePayload);
    const ripsPackPayloads = bundleItems?.filter(isRipsPackBundleItem).map((item) => item.bundlePayload) ?? [];
    const extraOfferPayloads = bundleItems?.filter(isExtraOfferBundleItem).map((item) => item.bundlePayload) ?? [];
    const metagameFeaturePayloads = bundleItems?.filter(isMetaGameFeatureBundleItem).map((item) => item.bundlePayload);

    // Merge metagame rewards by metagameTypeId and coinType, then sum amounts
    const metagameItems = bundleItems?.filter(isMetaGameBundleItem) || [];
    const metagamesRewardsPayloads = metagameItems.reduce((acc, item) => {
      const payload = item.bundlePayload;
      const existing = acc.find((p) => p.metagameTypeId === payload.metagameTypeId && p.coinType === payload.coinType);

      if (existing) {
        existing.amount += payload.amount;
      } else {
        acc.push({ ...payload });
      }

      return acc;
    }, [] as MetaGameTokenPayload[]);

    const gcAmount = rewards?.reduce((acc, pkg) => acc + pkg.gcCoin, 0) || 0;
    const scAmount = rewards?.reduce((acc, pkg) => acc + pkg.scCoin, 0) || 0;

    // Helper to create coin reward element
    const createCoinReward = (type: RewardType, amount: number) => ({
      type,
      key: type,
      element: (
        <RewardMotionWrapper className={classNames(classes.rewardItem, classes[type])}>
          {type === 'SC' ? (
            <div className={classes.coinRewardWrapper}>
              <SweepCoin isPile />
              {amount.toLocaleString()}
            </div>
          ) : (
            <div className={classes.coinRewardWrapper}>
              <SocialCoin isPile />
              {amount.toLocaleString()}
            </div>
          )}
        </RewardMotionWrapper>
      ),
    });

    // Collect and sort rewards according to REWARDS_ORDER
    const rewardElements: { type: RewardType; key: string; element: React.ReactNode }[] = [
      ...(gcAmount ? [createCoinReward('GC', gcAmount)] : []),
      ...(scAmount ? [createCoinReward('SC', scAmount)] : []),
      ...snappyGiftPayloads.map((payload, index) => ({
        type: 'snappyGift' as RewardType,
        key: `snappy-gift-${index}`,
        element: (
          <RewardMotionWrapper className={classes.snappyGift}>
            <SnappyGiftReward snappyGiftPayload={payload} />
          </RewardMotionWrapper>
        ),
      })),
      ...extraOfferPayloads.map((payload, index) => ({
        type: 'extraOffer' as RewardType,
        key: `extra-offer-${index}`,
        element: (
          <RewardMotionWrapper className={classes.snappyGift}>
            <ExtraOfferRewardComponent
              extraPercentage={payload.extraPercentage}
              rewardImageUrl={payload.rewardImageUrl}
            />
          </RewardMotionWrapper>
        ),
      })),
      ...metagamesRewardsPayloads.map((payload, index) => ({
        type: 'metagame' as RewardType,
        key: `metagame-${index}`,
        element: (
          <RewardMotionWrapper className={classes.metagame}>
            <MetaGameRewardComponent
              amount={payload.amount}
              className={classes.metagameReward}
              currencySymbol={payload.currencySymbol}
              fontSize={metagameFontSize}
            />
          </RewardMotionWrapper>
        ),
      })),
      ...metagameFeaturePayloads.map((payload, index) => ({
        type: 'metagameFeature' as RewardType,
        key: `metagameFeature-${index}`,
        element: (
          <RewardMotionWrapper className={classes.metagame}>
            <MetaGameFeatureRewardComponent
              amount={payload.amount}
              className={classes.metagameReward}
              fontSize={metagameFontSize}
              symbolUrl={payload.symbolUrl}
            />
          </RewardMotionWrapper>
        ),
      })),
      ...freeSpinPayloads.map((payload, index) => ({
        type: 'freeSpins' as RewardType,
        key: `freeSpins-${index}`,
        element: (
          <RewardMotionWrapper className={classes.freeSpin}>
            <FRBWidget
              betAmount={payload.bet || 10}
              coinType={payload.coinType}
              displayCount={payload.displayCount}
              fontSize={freeSpinFontSize}
              gameImage={payload.gameImageUrl}
              provider={payload.provider}
              rounds={payload.rounds || 0}
              type={payload.type as 'freeSpins'}
              usePropFontSize={true}
              width={'80%'}
            />
          </RewardMotionWrapper>
        ),
      })),
      ...challengeRewardPayloads.map((payload, index) => ({
        type: 'challenge' as RewardType,
        key: `challenge-${index}`,
        element: (
          <RewardMotionWrapper className={classes.challenge}>
            <ChallengeRewardComponent className={classes.challengeReward} symbolUrl={payload.symbolUrl} />
          </RewardMotionWrapper>
        ),
      })),
      ...ripsPackPayloads.map((payload, index) => ({
        type: 'ripsPack' as RewardType,
        key: `rips-pack-${index}`,
        element: (
          <RewardMotionWrapper
            className={classes.ripsPack}
            variants={hasCrownRipsWidget ? ripsPackItemVariants : undefined}
          >
            <div ref={index === 0 ? ripsPackTileRef : undefined} className={classes.ripsPackTile}>
              <RipsPackRewardComponent imageUrl={payload.imageUrl} name={payload.name} quantity={payload.quantity} />
            </div>
          </RewardMotionWrapper>
        ),
      })),
    ].sort((a, b) => {
      const indexA = REWARDS_ORDER.indexOf(a.type);
      const indexB = REWARDS_ORDER.indexOf(b.type);
      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });

    const totalItems = rewardElements.length;

    useEffect(() => {
      if (!claimRewardAnimationState.enable) {
        setShouldFadeUp(false);
        return;
      }

      const totalAnimationTime = (totalItems - 1) * STAGGER_DELAY + INITIAL_ANIMATION_DURATION + WAIT_TIME;
      const timer = setTimeout(() => {
        setShouldFadeUp(true);
      }, totalAnimationTime * 1000);

      return () => clearTimeout(timer);
    }, [claimRewardAnimationState.enable, totalItems]);

    const animationCompleteRef = useRef(false);

    const handleAnimationComplete = useCallback(() => {
      if (animationCompleteRef.current) {
        return;
      }
      animationCompleteRef.current = true;
      // Hand the pack tile to the fly-to-widget flourish BEFORE onEnd tears this overlay down: the
      // tile skipped the fade-up, so the clone swaps in at its exact rect and flies to the Crown
      // Rips widget once the claim dialog closes (mobile parity — coins float away, pack flies).
      // Only when the widget is actually on screen (the lobby); inside a slot the pack floated up
      // with the other tiles instead, so there's nothing to hand off.
      if (hasCrownRipsWidget) {
        const tileImg = ripsPackTileRef.current?.querySelector('img');
        const packImageUrl = rewards?.flatMap((pkg) => pkg.bundleItems).find(isRipsPackBundleItem)
          ?.bundlePayload.imageUrl;
        if (tileImg instanceof HTMLImageElement && packImageUrl) {
          const flyRequest = captureImgFlySource(tileImg, packImageUrl);
          if (flyRequest) {
            eventTriggerStore.requestRipsPackClaimFly(flyRequest);
          }
        }
      }
      void onEnd?.();
    }, [onEnd, rewards, eventTriggerStore, hasCrownRipsWidget]);

    // Handle animation complete - call onEnd when fadeUp animation finishes
    useEffect(() => {
      if (!shouldFadeUp || !onEnd) {
        animationCompleteRef.current = false;
        return;
      }

      // Calculate when the last item finishes fading out
      const fadeUpDuration = 0.5; // Duration from itemVariants fadeUp transition
      const fadeUpStaggerTime = totalItems > 0 ? (totalItems - 1) * FADE_UP_STAGGER_DELAY : 0;
      const totalFadeUpTime = (fadeUpDuration + fadeUpStaggerTime) * 1000;

      const timer = setTimeout(() => {
        animationStore.setAnimationBlockScreenVisible(false);
        handleAnimationComplete();
      }, totalFadeUpTime);

      return () => {
        clearTimeout(timer);
        animationCompleteRef.current = false;
      };
    }, [shouldFadeUp, totalItems, handleAnimationComplete, onEnd, animationStore]);

    useEffect(() => {
      if (refetchData) {
        void walletStore.fetchWallet();
        const hasMiniGames = freeSpinPayloads.some((item) =>
          ['Barouletta Gaming', 'Minigames'].includes(item.provider),
        );

        if (freeSpinPayloads.length > 0 && !hasMiniGames) {
          void userFreeSpinsStore.fetch();
        }
        if (metagamesRewardsPayloads.length > 0) {
          void metagamesStore.fetch();
        }
        if (snappyGiftPayloads.length > 0) {
          void userSnappyGiftsStore.fetch();
        }

        if (challengeRewardPayloads.length > 0) {
          void challengesStore.fetch();
        }
        if (ripsPackPayloads.length > 0) {
          void crownRipsStore.fetchPendingPacksCount();
        }
      }
    }, [
      refetchData,
      freeSpinPayloads,
      metagamesRewardsPayloads,
      walletStore,
      userFreeSpinsStore,
      metagamesStore,
      userSnappyGiftsStore,
      snappyGiftPayloads,
      challengeRewardPayloads,
      challengesStore,
      ripsPackPayloads,
      crownRipsStore,
    ]);

    useEffect(() => {
      if (enableSounds && enable) {
        playSound('rewards', {
          singleton: true,
          loop: false,
          delay: SOUND_DELAY,
        });
      }
    }, [enableSounds, playSound, enable]);

    // A rips pack has no representation on the old coins-fly path, so a claim containing one renders
    // this overlay even when the flag is off (useCoinsAnimation routes it here) — that's how the pack
    // gets shown at all. Non-pack claims stay gated on the flag.
    if (!isNewRewardsAnimationEnabled && ripsPackPayloads.length === 0) return null;

    return (
      <>
        {enable && (
          <div
            className={classNames(classes.claimRewardsAnimation, hideOverlay && classes.claimRewardsAnimationNoOverlay)}
          >
            <motion.div
              animate={shouldFadeUp ? 'fadeUp' : 'visible'}
              className={classes.rewardItems}
              initial="hidden"
              variants={containerVariants}
            >
              {rewardElements.map((reward) => (
                <React.Fragment key={reward.key}>{reward.element}</React.Fragment>
              ))}
            </motion.div>
          </div>
        )}
      </>
    );
  }
}

export const ClaimRewardsAnimation = observer(ClaimRewardsAnimationComponent);
